import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the ScheduleInterviewsContext
const ScheduleInterviewsContext = createContext();

// Custom hook to use the ScheduleInterviewsContext
export const useScheduleInterviews = () =>
  useContext(ScheduleInterviewsContext);

// Provider component
export const ScheduleInterviewsProvider = ({ children }) => {
  const [interviews, setInterviews] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/scheduled-interviews"; // Replace with your actual API URL
  const API_URL2 = "https://hrmspayroll.prepstripe.com/api/interviews/feedback"; // Replace with your actual API URL
  const token = getToken();

  // Fetch interviews from the API
  // const fetchInterviews = async (page = 1) => {
  //   setLoading(true);
  //   try {
  //     const response = await axios.get(API_URL, {
  //       headers: {
  //         Authorization: `Bearer ${getToken()}`,
  //       },
  //     });
  //     // console.log("fetch interviewS:", response.data?.data?.data || [])
  //     setInterviews(response.data?.data?.data || []); // Assuming interviews are in `data` key
  //     setPagination({
  //       currentPage: response.current_page,
  //       totalPages: response.last_page,
  //       totalItems: response.total,
  //       perPage: response.per_page,
  //     });
  //     setError(null);
  //   } catch (error) {
  //     console.error("Error fetching interviews:", error);
  //     setError(error.response?.data?.message || "Failed to fetch interviews.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Resolve list from API response (support multiple response shapes)
  const getListFromResponse = (response) => {
    const data = response?.data;
    const inner = data?.data;
    let list = inner?.data;
    if (Array.isArray(list)) return list;
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(data)) return data;
    return [];
  };

  // Normalize row so table columns (candidate, status, etc.) map correctly
  const normalizeInterviewRow = (item) => {
    if (!item) return item;
    const interview = item.interview || item;
    const candidate = item.candidate ?? interview.candidate;
    return { ...interview, candidate };
  };

  const fetchInterviews = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      await new Promise((r) => setTimeout(r, 1000));

      const rawList = getListFromResponse(response);
      const normalized = rawList.map(normalizeInterviewRow).reverse();
      setInterviews(normalized);

      const paginationSource = response?.data?.data || response?.data || {};
      setPagination({
        currentPage: paginationSource.current_page ?? 1,
        totalPages: paginationSource.last_page ?? 1,
        totalItems: paginationSource.total ?? 0,
        perPage: paginationSource.per_page ?? 10,
      });
      setError(null);
    } catch (error) {
      console.error("Error fetching interviews:", error);
      setError(error.response?.data?.message || "Failed to fetch interviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, [API_URL, token]); // Re-fetch only if API_URL or token changes

  // Add a new interview
  const addInterview = async (newInterview) => {
    // console.log("newInterview payload: ", newInterview)
    try {
      const response = await axios.post(API_URL, newInterview, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // console.log("Response after POST: ", response.data.data);

      // setInterviews((prev) => [...prev, response.data?.data]);
      // fetchInterviews()
      const interviewPayload = {
        ...response.data?.data?.interview,
        candidate: response.data?.data?.candidate,
      };

      setInterviews((prev) => [interviewPayload, ...prev]);
    } catch (error) {
      console.error("Error adding interview:", error);
      setError(error.response?.data?.message || "Failed to add interview.");
    }
  };

  // Update an existing interview
  const updateInterview = async (updatedInterview) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedInterview.id}`,
        updatedInterview,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setInterviews((prev) =>
        prev.map((interview) =>
          interview.id === updatedInterview.id ? response.data?.data : interview
        )
      );
    } catch (error) {
      console.error("Error updating interview:", error);
      setError(error.response?.data?.message || "Failed to update interview.");
    }
  };

  // Delete an interview
  const deleteInterview = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setInterviews((prev) => prev.filter((interview) => interview.id !== id));
    } catch (error) {
      console.error("Error deleting interview:", error);
      setError(error.response?.data?.message || "Failed to delete interview.");
    }
  };

  const updateInterviewStatus = async (interviewId, feedback) => {
    // console.log("interviewid: ", interviewId, "status: ", feedback.status, "feedback: ", feedback.overallFeedback)
    try {
      const response = await axios.post(
        `${API_URL2}`,
        {
          id: interviewId,
          status: feedback.status,
          feedback: feedback.overallFeedback,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      // Update the local state after a successful update
      // setInterviews((prevInterviews) =>
      //   prevInterviews.map((interview) =>
      //     interview.id === interviewId
      //       ? { ...interview, status: feedback.status }
      //       : interview
      //   )
      // );
      fetchInterviews();
      return response.data;
    } catch (error) {
      console.error("Error updating interview status:", error);
      throw error;
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      interviews,
      pagination,
      loading,
      error,
      addInterview,
      updateInterview,
      deleteInterview,
      fetchInterviews,
      updateInterviewStatus,
    }),
    [interviews, loading, error] // Recalculate the value only when these dependencies change
  );

  return (
    <ScheduleInterviewsContext.Provider value={value}>
      {children}
    </ScheduleInterviewsContext.Provider>
  );
};
